import { Section } from "../../../components/Section";
import { FormField, NumberInput, Select } from "../../../components/ui/FormComponents";
import { CurrencyField } from "../../../components/ui/CurrencyField";
import { MonthlyCalculationsDebug } from "../../../components/MonthlyCalculationsDebug";
import { PropertyLoanDetails } from "../../../types/mortgage";
import { LOAN_TYPES, REPAYMENT_TYPES } from "../../../constants";

interface PropertyLoanSectionProps {
  data: PropertyLoanDetails;
  onChange: (data: PropertyLoanDetails) => void;
  mortgageCalculations?: any;
  showValidation: boolean;
  isAdmin?: boolean;
}

export function PropertyLoanSection({ 
  data, 
  onChange, 
  mortgageCalculations, 
  showValidation, 
  isAdmin = false 
}: PropertyLoanSectionProps) {
  const updateField = <K extends keyof PropertyLoanDetails>(field: K, value: PropertyLoanDetails[K]) => {
    onChange({ ...data, [field]: value });
  };

  // Ensure loan type is always "Residential"
  if (data.loanType !== "Residential") {
    updateField("loanType", "Residential");
  }

  const loanToValue = data.propertyValue > 0 ? (data.loanRequired / data.propertyValue) * 100 : 0;

  // Validation errors
  const propertyValueError = showValidation && data.propertyValue <= 0 
    ? "Property value is required" 
    : undefined;

  const loanRequiredError = showValidation && (
    data.loanRequired < 20000000 || data.loanRequired > 500000000 // £200,000 to £5,000,000 in pennies
  ) ? "Loan amount must be between £200,000 and £5,000,000" 
    : undefined;

  const loanTermError = showValidation && data.loanTermMonths <= 0 
    ? "Loan term is required" 
    : undefined;

  const payRateError = showValidation && data.payRate <= 0 
    ? "Pay rate is required" 
    : undefined;

  const ltvError = showValidation && loanToValue > 80 
    ? "LTV must be 80% or less" 
    : undefined;

  return (
    <Section title="Property & Loan Details" colorTheme={0}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Property value" required error={propertyValueError}>
            <CurrencyField
              value={data.propertyValue}
              onChange={(value) => updateField("propertyValue", value)}
              placeholder="500000"
              error={!!propertyValueError}
            />
          </FormField>

          <FormField label="Loan amount required" required error={loanRequiredError}>
            <CurrencyField
              value={data.loanRequired}
              onChange={(value) => updateField("loanRequired", value)}
              placeholder="400000"
              error={!!loanRequiredError}
            />
          </FormField>

					<FormField label="Loan to Value (LTV)" error={ltvError}>
            <div className="relative">
              <NumberInput
                value={parseFloat(loanToValue.toFixed(2))}
                onChange={() => {}} // No-op since it's readonly
                disabled={true}
                placeholder="0.00"
                error={!!ltvError}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">%</span>
              </div>
            </div>
          </FormField>

          <FormField label="Loan term (years)" required error={loanTermError}>
            <NumberInput
              value={Math.round(data.loanTermMonths / 12)}
              onChange={(value) => updateField("loanTermMonths", Math.round(value) * 12)}
              placeholder="25"
              step={1}
              min={1}
              max={50}
              error={!!loanTermError}
            />
          </FormField>

          <FormField label="Loan pay rate (%)" required error={payRateError}>
            <NumberInput
              value={data.payRate}
              onChange={(value) => updateField("payRate", value)}
              placeholder="5.5"
              step={0.01}
              min={0}
              max={20}
              error={!!payRateError}
            />
            <div className="mt-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Enter the interest rate for the product you are considering. See our Product Guide{' '}
                    <a 
                      href="https://mhbs.co.uk/document/mhbs-intermediary-product-guide/" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-subheader"
                    >
                      here
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </FormField>

        </div>

        {/* Second row for repayment type and interest only amount */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* 5 Year Fixed Term Toggle - Hidden for future functionality */}
          {/* <FormField label="5 Year Fixed Term">
          <div className="flex">
            <button
              type="button"
              onClick={() => updateField("fiveYearFixedTerm", true)}
              className={`flex-1 px-3 py-2 text-sm border border-r-0 rounded-l-md transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                data.fiveYearFixedTerm
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Yes
            </button>

            <button
              type="button"
              onClick={() => updateField("fiveYearFixedTerm", false)}
              className={`flex-1 px-3 py-2 text-sm border rounded-r-md transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                !data.fiveYearFixedTerm
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              No
            </button>
          </div>
          </FormField> */}
          <FormField label="Repayment type">
            <Select
              value={data.repaymentType}
              onChange={(value) => updateField("repaymentType", value as "Repayment" | "Interest Only" | "Part and Part")}
              options={[...REPAYMENT_TYPES]}
            />
          </FormField>

					{/* Interest Only Amount - Only show for Part and Part */}
        	{data.repaymentType === "Part and Part" ? (          
            <FormField label="Interest only amount">
              <CurrencyField
                value={data.amountInterestOnly}
                onChange={(value) => updateField("amountInterestOnly", value)}
                placeholder="0"
              />
            </FormField>
        	) : (
            <div>{/* Placeholder to maintain grid layout */}</div>
          )}

        </div>

        {/* Information box for Interest Only and Part and Part mortgages */}
        {(data.repaymentType === "Interest Only" || data.repaymentType === "Part and Part") && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-subheader text-blue-800 dark:text-blue-200">
                  Interest Only Mortgage Requirements
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <p>
                    Where the proposed repayment strategy for an interest only mortgage is sale of the subject property, 
                    it must have a minimum of two bedrooms and (after deducting all secured loans) at least £300,000 equity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Section>
  );
}
